import React, { ReactElement } from 'react';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import {
    useDeleteParkingSharingAvailability,
    useDeleteParkingSharingRequest,
    useEditParkingSharingAvailability,
    useEditParkingSharingRequest,
} from '_api/useParkingSharing';
import { useAppNavigation } from '_navigator';
import { HeimeText, Modal } from 'Components';
import { BookingSharingAvailability, BookingSharingRequest } from 'types/BookingSharing';

type DeleteBookingSharingModalProps = {
    onDismiss: () => void;
} & (
    | {
          type: 'request';
          item: BookingSharingRequest;
      }
    | {
          type: 'availability';
          item: BookingSharingAvailability;
      }
);

const DeleteBookingSharingModal = ({ onDismiss, type, item }: DeleteBookingSharingModalProps): ReactElement => {
    const { t } = useTranslation();
    const { navigate, goBack, canGoBack } = useAppNavigation();

    const { mutate: deleteAvailability, isPending: isDeletingAvailability } = useDeleteParkingSharingAvailability();
    const { mutate: deleteRequest, isPending: isDeletingRequest } = useDeleteParkingSharingRequest();
    const { mutate: updateRequest, isPending: isUpdatingRequest } = useEditParkingSharingRequest();
    const { mutate: updateAvailability, isPending: isUpdatingAvailability } = useEditParkingSharingAvailability();

    const handleDelete = () => {
        const onSuccess = () => {
            onDismiss();
            if (canGoBack()) {
                goBack();
            } else {
                navigate('BookingSharingCategory', undefined);
            }
        };
        const isOngoing = moment(item.start_at).isBefore(moment().endOf('minute'));
        if (type === 'availability') {
            if (isOngoing) {
                updateAvailability(
                    [
                        item.id,
                        { ...item, end_at: moment().endOf('minute').toISOString(), request_for_approval_id: null },
                    ],
                    { onSuccess },
                );
            } else {
                deleteAvailability(item.id, { onSuccess });
            }
        } else {
            if (isOngoing) {
                updateRequest(
                    [
                        item.id,
                        {
                            ...item,
                            end_at: moment().endOf('minute').toISOString(),
                            requested_availability_slot_id: null,
                        },
                    ],
                    { onSuccess },
                );
            } else {
                deleteRequest(item.id, { onSuccess });
            }
        }
    };

    return (
        <Modal
            onDismiss={onDismiss}
            header={t('bookingSharing:confirm_delete')}
            content={
                type === 'availability' && item.assigned_requests.length > 0 ? (
                    <HeimeText variant="subtitle">
                        {t('bookingSharing:deleting_availability_warning', { count: item.assigned_requests.length })}
                    </HeimeText>
                ) : undefined
            }
            buttons={[
                { text: t('bookingSharing:cancel'), onPress: onDismiss },
                {
                    text: t('bookingSharing:delete'),
                    onPress: handleDelete,
                    type: 'danger',
                    isLoading:
                        isDeletingAvailability || isDeletingRequest || isUpdatingAvailability || isUpdatingRequest,
                },
            ]}
        />
    );
};

export default DeleteBookingSharingModal;
